import { Link } from 'react-router-dom';
import { formatMoney } from '../money';

interface Tally {
  count: number;
  amount: string;
}
export interface MoneySummary {
  to_invoice: Tally;
  held: Tally;
  invoices: { draft: Tally; sent: Tally; authorised: Tally };
  to_bill: Tally;
  bills: { received: Tally; matched: Tally; disputed: Tally };
  owed_to_you: string;
  you_owe: string;
  margin: string;
  overdue: Tally;
  paid_in: Tally;
  paid_out: Tally;
  to_invoice_clients: number;
  to_bill_contractors: number;
  ready_incl_gst: string;
  counts: Record<string, number>;
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const sum = (...xs: string[]) =>
  (xs.reduce((s, x) => s + Math.round(Number(x) * 100), 0) / 100).toFixed(2);

const invoices = (filter: string) => `/money?tab=invoices&filter=${filter}`;
const bills = (filter: string) => `/money?tab=bills&filter=${filter}`;

interface Step {
  label: string;
  amount: string;
  to: string;
  /** Something about this step that wants attention, e.g. the overdue part of what is waiting. */
  flag?: string;
}

/** One side of the money, in the order it moves: still to send out, sent and waiting, settled. */
function Flow(props: { title: string; steps: Step[] }) {
  return (
    <div className="flow">
      <h3>{props.title}</h3>
      <ol className="flow-steps">
        {props.steps.map((s) => (
          <li key={s.label}>
            <Link to={s.to}>
              <span className="flow-label">{s.label}</span>
              <span className="flow-amount">{formatMoney(s.amount)}</span>
              {s.flag && <span className="flow-flag">{s.flag}</span>}
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}

interface Job {
  key: string;
  title: string;
  detail: string;
  amount: string;
  action: { to: string; label: string };
  urgent?: boolean;
}

/**
 * Where the money stands and what to do about it. The strip follows the money
 * both ways: from clients (not invoiced yet, invoiced and waiting, paid in) and
 * to contractors (not billed yet, billed and owed, paid out), with the margin
 * between. Below it, the work waiting, the most urgent first, each with the run
 * or list it is done from.
 */
export function MoneyOverview(props: { summary: MoneySummary }) {
  const s = props.summary;
  const drafts = s.invoices.draft.count + s.invoices.sent.count;
  const jobs: Job[] = [
    {
      key: 'disputed',
      title: `${plural(s.bills.disputed.count, 'bill')} ${s.bills.disputed.count === 1 ? "doesn't" : "don't"} match`,
      detail: 'what their visits pay, so they wait to be sorted out',
      amount: s.bills.disputed.amount,
      action: { to: bills('disputed'), label: 'See the differences' },
      urgent: true,
    },
    {
      key: 'invoice',
      title: 'Invoice run',
      detail: [
        `${plural(s.to_invoice_clients, 'client')} · ${plural(s.to_invoice.count, 'visit')} ready`,
        s.held.count > 0 && `${s.held.count} held back`,
      ]
        .filter(Boolean)
        .join(' · '),
      amount: s.to_invoice.amount,
      action: { to: '/money/invoice-run', label: 'Start the invoice run' },
    },
    {
      key: 'bill',
      title: 'Bill run',
      detail: `${plural(s.to_bill_contractors, 'contractor')} · ${plural(s.to_bill.count, 'visit')} to bill`,
      amount: s.to_bill.amount,
      action: { to: '/money/bill-run', label: 'Start the bill run' },
    },
    {
      key: 'overdue',
      title: `${plural(s.overdue.count, 'invoice')} overdue`,
      detail: 'past their due date and not paid',
      amount: s.overdue.amount,
      action: { to: invoices('overdue'), label: 'See them' },
      urgent: true,
    },
    {
      key: 'ready',
      title: `${plural(s.bills.matched.count, 'bill')} ready to pay`,
      detail: 'including GST',
      amount: s.ready_incl_gst,
      action: { to: bills('matched'), label: 'Pay them' },
    },
    {
      key: 'check',
      title: `${plural(s.bills.received.count, 'bill')} to check`,
      detail: 'sent in by contractors',
      amount: s.bills.received.amount,
      action: { to: bills('received'), label: 'Check them' },
    },
    {
      key: 'drafts',
      title: `${plural(drafts, 'draft invoice')}`,
      detail: 'to approve and send to the client',
      amount: sum(s.invoices.draft.amount, s.invoices.sent.amount),
      action: { to: invoices('draft'), label: 'See the drafts' },
    },
  ];
  const counts: Record<string, number> = {
    disputed: s.bills.disputed.count,
    invoice: s.to_invoice.count,
    bill: s.to_bill.count,
    overdue: s.overdue.count,
    ready: s.bills.matched.count,
    check: s.bills.received.count,
    drafts,
  };
  const waiting = jobs.filter((j) => counts[j.key]! > 0);

  return (
    <div className="money-tab money-home">
      <section className="money-flow" aria-labelledby="flow-h">
        <h2 id="flow-h" className="sr-only">
          Where the money is
        </h2>
        <Flow
          title="From clients"
          steps={[
            {
              label: 'Not invoiced yet',
              amount: sum(s.to_invoice.amount, s.held.amount),
              to: '/money/invoice-run',
            },
            {
              label: 'Invoiced, waiting',
              amount: sum(
                s.invoices.draft.amount,
                s.invoices.sent.amount,
                s.invoices.authorised.amount,
              ),
              to: invoices(''),
              flag: s.overdue.count > 0 ? `${formatMoney(s.overdue.amount)} overdue` : undefined,
            },
            { label: 'Paid in, last 30 days', amount: s.paid_in.amount, to: invoices('paid') },
          ]}
        />
        <Flow
          title="To contractors"
          steps={[
            { label: 'Not billed yet', amount: s.to_bill.amount, to: '/money/bill-run' },
            {
              label: 'Billed, to pay',
              amount: sum(s.bills.received.amount, s.bills.matched.amount, s.bills.disputed.amount),
              to: bills(''),
              flag:
                s.bills.disputed.count > 0
                  ? `${formatMoney(s.bills.disputed.amount)} doesn't match`
                  : undefined,
            },
            { label: 'Paid out, last 30 days', amount: s.paid_out.amount, to: bills('paid') },
          ]}
        />
        <p className="flow-margin">
          <span>Margin on finished work</span> <strong>{formatMoney(s.margin)}</strong>{' '}
          <span className="muted">
            what finished visits charge less what they pay. All amounts ex GST unless they say so.
          </span>
        </p>
      </section>

      <section className="money-jobs" aria-labelledby="jobs-h">
        <h2 id="jobs-h">To do</h2>
        {waiting.length === 0 ? (
          <div className="empty-state">
            <h3>All caught up</h3>
            <p>
              Every finished visit is invoiced and billed, and every bill is settled. New work shows
              up here as visits are done.
            </p>
          </div>
        ) : (
          <ol className="jobs-list">
            {waiting.map((j, i) => (
              <li key={j.key} className={j.urgent ? 'job-row urgent' : 'job-row'}>
                <div className="job-what">
                  <strong>{j.title}</strong>
                  <span className="muted">{j.detail}</span>
                </div>
                <span className="job-amount">{formatMoney(j.amount)}</span>
                {/* The most urgent job carries the one primary action on the page. */}
                <Link className={i === 0 ? 'button' : 'button quiet-button'} to={j.action.to}>
                  {j.action.label}
                </Link>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
